import { ofType, StateObservable } from 'redux-observable';
import { ajax } from 'rxjs/ajax';
import {Observable, of, timer} from 'rxjs';
import {mergeMap, map, catchError, debounce} from 'rxjs/operators';
import { APP_CONFIG } from "../../config/GeneralConfig";
import { eventNotificationShow } from "./EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import { AuthenticatedUser } from '../../models/users/AuthenticatedUser';
import {RouteMethods, basicRequestHeaders, decodeJwtPayload, setAuthToken} from "../../config/RouteConfig";
import {currentLanguageFromStore, Language} from "../../config/LanguageConfig";
import {createErrorResponse} from "../../config/languages/definitions/AjaxResponses";
import {AppState} from "../../reducers/RootReducer";
import {sendUserToPage} from "./UserViewSettingsActions";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {getLocalStorage, setLocalStorage} from "../../helpers/Storage";
import {Identifier} from "../../models/common/Identifier";

export const localStorageTenantIdKey = "tenantId";

/**
 * typescript declarations
 */
interface FetchAuthenticatedUserAction extends ReduxAction<typeof FETCH_AUTHENTICATED_USER> {
    debounceTime: number
}
interface FetchAuthenticatedUserSuccessAction {
    type: typeof FETCH_AUTHENTICATED_USER_SUCCESS
    authenticatedUser: AuthenticatedUser
}
interface FetchAuthenticatedUserErrorAction {
    type: typeof FETCH_AUTHENTICATED_USER_ERROR
    error: any
}

export type AuthenticatedUserActionTypes = FetchAuthenticatedUserAction | FetchAuthenticatedUserSuccessAction | FetchAuthenticatedUserErrorAction


/**
 * fetch logged in user action constants
 */
export const FETCH_AUTHENTICATED_USER = "FETCH_AUTHENTICATED_USER";
export const FETCH_AUTHENTICATED_USER_SUCCESS = "FETCH_AUTHENTICATED_USER_SUCCESS";
export const FETCH_AUTHENTICATED_USER_ERROR = "FETCH_AUTHENTICATED_USER_ERROR";

/**
 * initiates the fetching process of the logged in user
 */
export const fetchAuthenticatedUser = ( debounceTime: number = 0 ): FetchAuthenticatedUserAction => ({ type: FETCH_AUTHENTICATED_USER, debounceTime });

/**
 * after epic completes successfully, this function is called, to send the response to the reducer
 */
export const fetchAuthenticatedUserSuccess = ( authenticatedUser: AuthenticatedUser ): FetchAuthenticatedUserSuccessAction => ({ type: FETCH_AUTHENTICATED_USER_SUCCESS, authenticatedUser });

/**
 * clalled when epic catches an error. the error will be send to the reducer
 */
export const fetchAuthenticatedUserError = ( error: any ): FetchAuthenticatedUserErrorAction => ({ type: FETCH_AUTHENTICATED_USER_ERROR, error });

/**
 * the fetchAuthenticatedUser will be called every n seconds to revalidate the token on the backend
 */
const REVALIDATE_AUTH_TIME_IN_SECONDS: number = 360;

/**
 * epic to perform the fetching and call success or error upon events. don't call this function directly
 *
 * @param action$
 */
export const fetchAuthenticatedUserEpic = (
    action$: Observable< FetchAuthenticatedUserAction >
) =>
    action$.pipe(
        ofType( FETCH_AUTHENTICATED_USER ),
        debounce( action => timer( action.debounceTime ) ),
        mergeMap( action => {
            const path = APP_CONFIG.baseWeb + "user/authenticated/";
            let isUserLoggedIn = false;
//console.log(APP_CONFIG.getRequestObject(path));
            return ajax( APP_CONFIG.getRequestObject( path ) )
                    .pipe(
                        mergeMap( (response, index) => {

                            const authUser = response.response as AuthenticatedUser;

                            if( authUser.id.rawValue > 0 ) {
                                isUserLoggedIn = true;
                                return of(
                                    fetchAuthenticatedUserSuccess( authUser ),
                                    //fetchAuthenticatedUser( REVALIDATE_AUTH_TIME_IN_SECONDS * 1000 )
                                );
                            } else {
                                isUserLoggedIn = false;
                                if( action.debounceTime === 0 ) {
                                    const tenantId = getLocalStorage(localStorageTenantIdKey, "");

                                    
                                    return of(
                                        fetchAuthenticatedUserSuccess( authUser ),
                                        userTokenRefresh(tenantId)
                                    );
                                } else {
                                    throw Error("Sie wurden ausgeloggt.");
                                }

                            }

                        }),

                        catchError(error => {
                            return of(
                                fetchAuthenticatedUserError(error),
                                userLogout(),
                                eventNotificationShow(
                                    EVENT_NOTIFICATION_TYPE.customAjaxError,
                                    'Daten konnten nicht abgerufen werden, Fehlercode: '
                                ),
                                sendUserToPage( '/login', '' )
                            );
                    })


            );

        }),
    );

  
/**
  * user login typescript redux action interfaces
  */
export interface UserLoginAction {
    type: typeof USER_LOGIN
    mail: string
    password: string
    redirectLocation: string
}

export interface UserLoginSuccessAction {
    type: typeof USER_LOGIN_SUCCESS
    authenticatedUser: AuthenticatedUser
}

export interface UserLoginErrorAction {
    type: typeof USER_LOGIN_ERROR
    error: Error
} 

export type UserLoginActionTypes = UserLoginAction | UserLoginErrorAction | UserLoginSuccessAction 

export const USER_LOGIN = "USER_LOGIN";
export const USER_LOGIN_SUCCESS = "USER_LOGIN_SUCCESS";
export const USER_LOGIN_ERROR = "USER_LOGIN_ERROR";

export function userLogin(
    mail: string,
    password: string,
    redirectLocation: string
): UserLoginAction {
    return { type: USER_LOGIN, mail, password, redirectLocation };
}
export const userLoginSuccess = ( response: { authenticatedUser: AuthenticatedUser, token: string} ): UserLoginSuccessAction => {
    setAuthToken(response.token);

    const claims = decodeJwtPayload(response.token);
    //@ts-ignore
    setLocalStorage( localStorageTenantIdKey, claims.tenantId);

    return ({type: USER_LOGIN_SUCCESS, authenticatedUser: response.authenticatedUser});
}
export const userLoginError = ( error: Error ): UserLoginErrorAction => ({ type: USER_LOGIN_ERROR, error });

function getLoginErrorMessage( errorNo: number, language: Language ) {
    switch( errorNo ) {
        case 400: return language.ajaxResponse.errors.loginWrongPassword
        case 401: return language.ajaxResponse.errors.loginUserNotFound
        default: return createErrorResponse( language, errorNo, 'Login', 'userLogin' )
    }
}

/**
 * Handles user login actions by processing credentials and updating the application state.
 *
 * This function exists to manage the user login lifecycle, including server authentication,
 * handling responses, and managing errors. It facilitates the user login flow seamlessly
 * while providing appropriate feedback to the application for further processing.
 *
 * The method listens for `USER_LOGIN` actions, sends login credentials to the server,
 * and processes the response. Upon success, it dispatches actions to update the application
 * state and navigates the user. If the login fails, it handles error scenarios by dispatching
 * appropriate actions and displaying notifications.
 *
 * @param action$ An observable stream of login action objects, where each action contains
 *   user credentials (`mail`, `password`) and a `redirectLocation`. The `redirectLocation` determines
 *   where to navigate the user after a successful login.
 * @param store$ A state observable containing the current application state, used to access
 *   contextual information like the user's preferred language.
 * @return An observable stream of one or more actions to update the application state
 *   (e.g., on success: `userLoginSuccess`, `fetchAuthenticatedUser`, `sendUserToPage`;
 *   on failure: `userLoginError`, `eventNotificationShow`).
 * @throws Will generate an error notification if the login process fails due to
 *   network issues, server-side errors, or invalid credentials. Specific error messages
 *   are determined based on the server response and user's language.
 */
export function userLoginEpic (
    action$: Observable< UserLoginAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(USER_LOGIN),
        mergeMap(action => {

            const path = APP_CONFIG.baseWeb + "user/login/";

            return ajax(APP_CONFIG.getRequestObject(
                path,
                RouteMethods.post,
                {
                    mail: action.mail,
                    password: action.password
                },
                {
                    ...basicRequestHeaders(),
                })
            ).pipe(
                mergeMap(response => of(
                    userLoginSuccess( response.response as { authenticatedUser: AuthenticatedUser, token: string } ),
                    fetchAuthenticatedUser(),
                    sendUserToPage( action.redirectLocation , "index")
                 )),
                catchError(error => {
                    //console.log( error );
                    return of(
                        userLoginError(error),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            getLoginErrorMessage(
                                error.status,
                                currentLanguageFromStore( store$ )
                            )
                        )
                    );
                })
            );
        })
    );
}

/**
 * REFRESH TOKEN
 */
export interface UserRefreshAction extends ReduxAction<typeof USER_REFRESH> {
    tenantId: Identifier<string>
}

export interface UserRefreshSuccessAction extends ReduxAction<typeof USER_REFRESH_SUCCESS>{
    authenticatedUser: AuthenticatedUser
}

export interface UserRefreshErrorAction extends ErrorAction<typeof USER_REFRESH_ERROR> {

}

export type UserRefreshActionTypes = UserRefreshAction | UserRefreshErrorAction | UserRefreshSuccessAction

export const USER_REFRESH = "USER_REFRESH";
export const USER_REFRESH_SUCCESS = "USER_REFRESH_SUCCESS";
export const USER_REFRESH_ERROR = "USER_REFRESH_ERROR";

export function userTokenRefresh(tenantId: Identifier<string> ): UserRefreshAction {
    return { type: USER_REFRESH, tenantId };
}
export const userTokenRefreshSuccess = (response: { authenticatedUser: AuthenticatedUser, token: string}): UserRefreshSuccessAction => {
    setAuthToken(response.token);

    return ({type: USER_REFRESH_SUCCESS, authenticatedUser: response.authenticatedUser});
}
export const userTokenRefreshError = (error: Error ): UserRefreshErrorAction =>
    ({ type: USER_REFRESH_ERROR, error });


/**
 * Handles user refresh actions by processing credentials and updating the application state.
 *
 * This function exists to manage the user refresh lifecycle, including server authentication,
 * handling responses, and managing errors. It facilitates the user refresh flow seamlessly
 * while providing appropriate feedback to the application for further processing.
 *
 * The method listens for `USER_REFRESH` actions, sends refresh credentials to the server,
 * and processes the response. Upon success, it dispatches actions to update the application
 * state and navigates the user. If the refresh fails, it handles error scenarios by dispatching
 * appropriate actions and displaying notifications.
 *
 * @param action$ An observable stream of refresh action objects, where each action contains
 *   user credentials (`mail`, `password`) and a `redirectLocation`. The `redirectLocation` determines
 *   where to navigate the user after a successful refresh.
 * @param store$ A state observable containing the current application state, used to access
 *   contextual information like the user's preferred language.
 * @return An observable stream of one or more actions to update the application state
 *   (e.g., on success: `userRefreshSuccess`, `fetchAuthenticatedUser`, `sendUserToPage`;
 *   on failure: `userRefreshError`, `eventNotificationShow`).
 * @throws Will generate an error notification if the refresh process fails due to
 *   network issues, server-side errors, or invalid credentials. Specific error messages
 *   are determined based on the server response and user's language.
 */
export function userRefreshEpic (
    action$: Observable< UserRefreshAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(USER_REFRESH),
        mergeMap(action => {

            const path = APP_CONFIG.baseWeb + "user/refresh-token/";

            return ajax(APP_CONFIG.getRequestObject(
                path,
                RouteMethods.post,
                {
                    tenantId: action.tenantId.rawValue
                },
                {
                    ...basicRequestHeaders(),
                })
            ).pipe(
                mergeMap(response => of(
                    userTokenRefreshSuccess( response.response as { authenticatedUser: AuthenticatedUser, token: string } ),
                    fetchAuthenticatedUser(),
                )),
                catchError(error => {
                    //console.log( error );
                    return of();
                })
            );
        })
    );
}


/**
 * USER LOGOUT
 */
interface UserLogoutAction {
    type: typeof USER_LOGOUT
}
interface UserLogoutSuccessAction {
    type: typeof USER_LOGOUT_SUCCESS
    authUser: AuthenticatedUser
}
interface UserLogoutErrorAction {
    type: typeof USER_LOGOUT_ERROR
    error: Error
}
export type UserLogoutActionTypes = UserLogoutAction | UserLogoutSuccessAction | UserLogoutErrorAction

export const USER_LOGOUT = "USER_LOGOUT";
export const USER_LOGOUT_SUCCESS = "USER_LOGOUT_SUCCESS";
export const USER_LOGOUT_ERROR = "USER_LOGOUT_ERROR";

export const userLogout = (): UserLogoutAction => ({ type: USER_LOGOUT });
export const userLogoutSuccess = (authUser: AuthenticatedUser): UserLogoutSuccessAction => ({ type: USER_LOGOUT_SUCCESS, authUser });
export const userLogoutError = (error: Error): UserLogoutErrorAction => ({ type: USER_LOGOUT_ERROR, error });

export const userLogoutEpic = ( action$: Observable< UserLogoutAction > ) =>
    action$.pipe(
        ofType( USER_LOGOUT ),
        mergeMap( _ => {
            const path = APP_CONFIG.baseWeb + "user/logout";
            return ajax(APP_CONFIG.getRequestObject(
                    path, 
                    RouteMethods.post
                )
            ).pipe(
                map(response => userLogoutSuccess( response.response as AuthenticatedUser )),
                catchError(error => {
                    return of(
                        userLogoutError(error),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            'Login nicht erfolgreich!, Fehlercode: '
                        )
                    );
                })
            );
        })
    );